import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import HrDashboardLayout from '../components/layouts/HrDashboardLayout';
import Icon from '../components/atoms/Icon';
import Button from '../components/atoms/Button';
import Input from '../components/atoms/Input';
import SessionManager from '../utils/sessionManager';
import { getJobs, createJob, updateJob, deleteJob, updateJobStatus } from '../services/api';

const initialJobForm = {
  title: '',
  location: '',
  description: '',
  employmentType: 'Full-time',
  workType: 'on-site',
  experienceLevel: 'mid',
  department: 'Product Engineering',
  category: 'IT',
  tags: '',
  jdPdfFile: null,
  totalQuestions: 5,
  technicalQuestions: 3,
  behavioralQuestions: 1,
  situationalQuestions: 1,
  additionalRequirements: '',
  atsPassThreshold: 50,
  autoRejectEnabled: false,
  autoRejectThreshold: 70,
  liveInterviewEnabled: false,
  liveInterviewDuration: 5,
  liveInterviewLanguage: 'en-US',
  liveInterviewFocus: ['technical', 'behavioral'],
};

const HrJobsPage = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [jobFormOpen, setJobFormOpen] = useState(false);
  const [jobForm, setJobForm] = useState(initialJobForm);
  const [jobSubmitting, setJobSubmitting] = useState(false);
  const [jobFormError, setJobFormError] = useState('');
  const [editingJob, setEditingJob] = useState(null);
  const [editJobForm, setEditJobForm] = useState(initialJobForm);
  const [editJobSubmitting, setEditJobSubmitting] = useState(false);
  const [editJobError, setEditJobError] = useState('');
  const [deleteConfirmJob, setDeleteConfirmJob] = useState(null);

  // Get HR company name from session
  const getHrCompanyName = () => {
    SessionManager.migrateLegacySession();
    const hrSession = SessionManager.getSession('hr');
    return hrSession?.company || hrSession?.profile?.company_name || 'Company';
  };

  const companyName = getHrCompanyName();

  // Check HR authentication
  useEffect(() => {
    const isLoggedInAsHr = SessionManager.isLoggedIn('hr');
    const currentSession = SessionManager.getCurrentSession();
    
    if (!isLoggedInAsHr || (currentSession && currentSession.role !== 'hr')) {
      navigate('/hr_login');
      return;
    }
    
    loadJobs();
  }, [navigate]);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const data = await getJobs({ company: companyName });
      
      console.log('Raw jobs from API:', data);
      
      // Normalize jobs to match old implementation
      const normalizedJobs = data.map(job => {
        const normalizedStatus = job.status || (job.is_open ? 'Active' : 'Closed');
        console.log(`Job ${job.id} (${job.title}): status=${job.status}, is_open=${job.is_open}, normalized=${normalizedStatus}`);
        return {
          ...job,
          status: normalizedStatus
        };
      });
      
      console.log('Normalized jobs:', normalizedJobs);
      console.log('Active jobs:', normalizedJobs.filter(j => j.status?.toLowerCase() === 'active'));
      console.log('Closed jobs:', normalizedJobs.filter(j => {
        const s = j.status?.toLowerCase();
        return s === 'closed' || s === 'inactive';
      }));
      
      setJobs(normalizedJobs);
    } catch (error) {
      console.error('Error loading jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCreateJob = async (e) => {
    e.preventDefault();
    
    if (!jobForm.title.trim() || !jobForm.location.trim() || !jobForm.description.trim()) {
      setJobFormError('Please fill in the title, location, and description.');
      return;
    }
    
    setJobSubmitting(true);
    setJobFormError('');

    try {
      // Convert JD PDF to base64 if provided
      let jdPdfUrl = null;
      if (jobForm.jdPdfFile) {
        try {
          jdPdfUrl = await convertFileToBase64(jobForm.jdPdfFile);
        } catch (error) {
          console.error('Error converting JD PDF:', error);
          setJobFormError('Failed to process JD PDF. Please try again.');
          setJobSubmitting(false);
          return;
        }
      }

      const payload = {
        title: jobForm.title.trim(),
        company: companyName,
        location: jobForm.location.trim(),
        description: jobForm.description.trim(),
        type: jobForm.employmentType,
        work_type: jobForm.workType,
        experience_level: jobForm.experienceLevel,
        tags: [
          jobForm.department.trim(),
          jobForm.category.trim(),
          ...jobForm.tags.split(',').map((tag) => tag.trim()),
        ].filter(Boolean),
        jd_pdf_url: jdPdfUrl,
        // Interview Configuration
        interview_config: {
          total_questions: jobForm.totalQuestions,
          technical_questions: jobForm.technicalQuestions,
          behavioral_questions: jobForm.behavioralQuestions,
          situational_questions: jobForm.situationalQuestions,
          additional_requirements: jobForm.additionalRequirements.trim(),
        },
        // ATS Pass Threshold
        ats_pass_threshold: jobForm.atsPassThreshold,
        // Auto-Rejection Configuration
        auto_reject_enabled: jobForm.autoRejectEnabled,
        auto_reject_threshold: jobForm.autoRejectThreshold,
        // Live Interview Configuration
        live_interview_enabled: jobForm.liveInterviewEnabled,
        live_interview_config: {
          duration_minutes: jobForm.liveInterviewDuration,
          language: jobForm.liveInterviewLanguage,
          focus_areas: jobForm.liveInterviewFocus,
        },
      };

      await createJob(payload);
      setJobForm(initialJobForm);
      setJobFormOpen(false);
      loadJobs();
    } catch (error) {
      setJobFormError(error.message || 'Unable to create job. Please try again.');
    } finally {
      setJobSubmitting(false);
    }
  };

  const handleUpdateJob = async (e) => {
    e.preventDefault();
    setEditJobSubmitting(true);
    setEditJobError('');

    try {
      const payload = {
        title: editJobForm.title.trim(),
        company: companyName,
        location: editJobForm.location.trim(),
        description: editJobForm.description.trim(),
        type: editJobForm.employmentType,
        work_type: editJobForm.workType,
        experience_level: editJobForm.experienceLevel,
        tags: [
          editJobForm.department?.trim(),
          editJobForm.category?.trim(),
          ...(editJobForm.tags || '').split(',').map((tag) => tag.trim()),
        ].filter(Boolean),
        interview_config: {
          total_questions: editJobForm.totalQuestions,
          technical_questions: editJobForm.technicalQuestions,
          behavioral_questions: editJobForm.behavioralQuestions,
          situational_questions: editJobForm.situationalQuestions,
          additional_requirements: editJobForm.additionalRequirements?.trim() || '',
        },
        ats_pass_threshold: editJobForm.atsPassThreshold,
        auto_reject_enabled: editJobForm.autoRejectEnabled,
        auto_reject_threshold: editJobForm.autoRejectThreshold,
        live_interview_enabled: editJobForm.liveInterviewEnabled,
        live_interview_config: {
          duration_minutes: editJobForm.liveInterviewDuration,
          language: editJobForm.liveInterviewLanguage,
          focus_areas: editJobForm.liveInterviewFocus,
        },
      };

      await updateJob(editingJob.id, payload);
      setEditingJob(null);
      setEditJobForm(initialJobForm);
      loadJobs();
    } catch (error) {
      console.error('Error updating job:', error);
      setEditJobError(error.message || 'Failed to update job. Please try again.');
    } finally {
      setEditJobSubmitting(false);
    }
  };

  const handleDeleteJob = async (jobId) => {
    try {
      await deleteJob(jobId);
      setDeleteConfirmJob(null);
      loadJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
    }
  };

  const handleStatusChange = async (jobId, newStatus) => {
    try {
      // Capitalize status to match backend expectation
      const statusToSend = newStatus.charAt(0).toUpperCase() + newStatus.slice(1).toLowerCase();
      
      // Optimistic update - update local state immediately
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === jobId ? { ...job, status: statusToSend } : job
        )
      );
      
      console.log(`Updating job ${jobId} status to: ${statusToSend}`);
      await updateJobStatus(jobId, statusToSend);
      
      // Reload to ensure consistency with backend
      await loadJobs();
    } catch (error) {
      console.error('Error updating job status:', error);
      // Reload on error to revert optimistic update
      await loadJobs();
    }
  };

  const openEditJobModal = (job) => {
    setEditingJob(job);
    setEditJobForm({
      title: job.title || '',
      location: job.location || '',
      description: job.description || '',
      employmentType: job.type || job.employment_type || 'Full-time',
      workType: job.work_type || 'on-site',
      experienceLevel: job.experience_level || 'mid',
      department: job.department || 'Product Engineering',
      category: job.category || 'IT',
      tags: Array.isArray(job.tags) ? job.tags.join(', ') : (job.tags || ''),
      jdPdfFile: null,
      totalQuestions: job.interview_config?.total_questions || 5,
      technicalQuestions: job.interview_config?.technical_questions || 3,
      behavioralQuestions: job.interview_config?.behavioral_questions || 1,
      situationalQuestions: job.interview_config?.situational_questions || 1,
      additionalRequirements: job.interview_config?.additional_requirements || '',
      atsPassThreshold: job.ats_pass_threshold || 50,
      autoRejectEnabled: job.auto_reject_enabled || false,
      autoRejectThreshold: job.auto_reject_threshold || 70,
      liveInterviewEnabled: job.live_interview_enabled || false,
      liveInterviewDuration: job.live_interview_config?.duration_minutes || 5,
      liveInterviewLanguage: job.live_interview_config?.language || 'en-US',
      liveInterviewFocus: job.live_interview_config?.focus_areas || ['technical', 'behavioral'],
    });
  };

  const filteredJobs = jobs.filter(job => {
    if (filter === 'all') return true;
    const jobStatus = (job.status || '').toLowerCase();
    if (filter === 'active') return jobStatus === 'active';
    if (filter === 'closed') return jobStatus === 'closed' || jobStatus === 'inactive';
    if (filter === 'draft') return jobStatus === 'draft';
    return true;
  });

  console.log(`Filter: ${filter}, Total jobs: ${jobs.length}, Filtered jobs: ${filteredJobs.length}`);

  const getStatusBadge = (status) => {
    const statusLower = (status || '').toLowerCase();
    const colors = {
      active: 'bg-green-100 text-green-700',
      closed: 'bg-gray-100 text-gray-700',
      inactive: 'bg-gray-100 text-gray-700',
      draft: 'bg-yellow-100 text-yellow-700'
    };
    return colors[statusLower] || colors.active;
  };

  const getJobCounts = () => {
    return {
      total: jobs.length,
      active: jobs.filter(j => (j.status || '').toLowerCase() === 'active').length,
      closed: jobs.filter(j => {
        const status = (j.status || '').toLowerCase();
        return status === 'closed' || status === 'inactive';
      }).length,
      draft: jobs.filter(j => (j.status || '').toLowerCase() === 'draft').length
    };
  };

  const counts = getJobCounts();

  if (loading) {
    return (
      <HrDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </HrDashboardLayout>
    );
  }

  return (
    <HrDashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Job Postings</h1>
            <p className="text-gray-600 mt-1">Manage your job openings and requirements</p>
          </div>
          <Button 
            onClick={() => setJobFormOpen(!jobFormOpen)} 
            className="bg-gray-900 hover:bg-gray-800 text-white"
          >
            <Icon name={jobFormOpen ? 'close' : 'add'} size={20} className="mr-2" />
            {jobFormOpen ? 'Close Form' : 'Post New Job'}
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Jobs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{counts.total}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon name="work" size={24} className="text-gray-700" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Jobs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{counts.active}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon name="check_circle" size={24} className="text-gray-700" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Closed Jobs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{counts.closed}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon name="archive" size={24} className="text-gray-700" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Draft Jobs</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{counts.draft}</p>
              </div>
              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                <Icon name="edit" size={24} className="text-gray-700" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'all' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Jobs ({counts.total})
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'active' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Active ({counts.active})
            </button>
            <button
              onClick={() => setFilter('closed')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === 'closed' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Closed ({counts.closed})
            </button>
          </div>
        </div>

        {/* Create Job Form */}
        {jobFormOpen && (
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Job Post</h3>
            {jobFormError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {jobFormError}
              </div>
            )}
            <form onSubmit={handleCreateJob} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Title *</label>
                  <Input
                    type="text"
                    value={jobForm.title}
                    onChange={(e) => setJobForm({ ...jobForm, title: e.target.value })}
                    placeholder="e.g. Senior Software Engineer"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
                  <Input
                    type="text"
                    value={jobForm.location}
                    onChange={(e) => setJobForm({ ...jobForm, location: e.target.value })}
                    placeholder="e.g. Bangalore, India"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                  <Input
                    type="text"
                    value={jobForm.department}
                    onChange={(e) => setJobForm({ ...jobForm, department: e.target.value })}
                    placeholder="e.g. Product Engineering"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={jobForm.category}
                    onChange={(e) => setJobForm({ ...jobForm, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="IT">IT</option>
                    <option value="Non-IT">Non-IT</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Description (Text) *</label>
                <textarea
                  value={jobForm.description}
                  onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
                  placeholder="Role description, responsibilities, skills..."
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Description PDF (Optional)
                  <span className="text-xs text-gray-500 ml-2">ATS will compare Resume PDF with JD PDF</span>
                </label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setJobForm({ ...jobForm, jdPdfFile: e.target.files[0] })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Upload JD as PDF for better ATS scoring. If both text and PDF are provided, PDF will be used for ATS calculation.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
                  <select
                    value={jobForm.employmentType}
                    onChange={(e) => setJobForm({ ...jobForm, employmentType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Work Type</label>
                  <select
                    value={jobForm.workType}
                    onChange={(e) => setJobForm({ ...jobForm, workType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="on-site">On-site</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
                  <select
                    value={jobForm.experienceLevel}
                    onChange={(e) => setJobForm({ ...jobForm, experienceLevel: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tags (comma separated)</label>
                <Input
                  type="text"
                  value={jobForm.tags}
                  onChange={(e) => setJobForm({ ...jobForm, tags: e.target.value })}
                  placeholder="e.g. Python, React, AWS"
                />
              </div>

              {/* Interview Configuration */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="quiz" size={20} className="text-blue-600" />
                  <h4 className="text-base font-semibold text-gray-900">Interview Configuration (Optional)</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">Customize the AI-generated interview questions for this role</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Total Questions</label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={jobForm.totalQuestions}
                      onChange={(e) => setJobForm({ ...jobForm, totalQuestions: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Technical Questions</label>
                    <Input
                      type="number"
                      min="0"
                      value={jobForm.technicalQuestions}
                      onChange={(e) => setJobForm({ ...jobForm, technicalQuestions: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Behavioral Questions</label>
                    <Input
                      type="number"
                      min="0"
                      value={jobForm.behavioralQuestions}
                      onChange={(e) => setJobForm({ ...jobForm, behavioralQuestions: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Situational Questions</label>
                    <Input
                      type="number"
                      min="0"
                      value={jobForm.situationalQuestions}
                      onChange={(e) => setJobForm({ ...jobForm, situationalQuestions: parseInt(e.target.value) })}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Requirements / Custom Prompt
                    <span className="text-xs text-gray-500 ml-2">Guide AI question generation</span>
                  </label>
                  <textarea
                    value={jobForm.additionalRequirements}
                    onChange={(e) => setJobForm({ ...jobForm, additionalRequirements: e.target.value })}
                    placeholder="E.g., Focus on cloud architecture experience, ask about microservices, candidate should have strong communication skills..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This will be used by AI to generate relevant interview questions and for resume matching (ATS scoring)
                  </p>
                </div>
              </div>

              {/* ATS Pass Threshold */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="verified" size={20} className="text-blue-600" />
                  <h4 className="text-base font-semibold text-gray-900">ATS Pass Threshold</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Minimum ATS score required for candidates to proceed to the interview assessment
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum ATS Score (%)
                  </label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={jobForm.atsPassThreshold}
                    onChange={(e) => setJobForm({ ...jobForm, atsPassThreshold: parseInt(e.target.value) || 50 })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Candidates with ATS score below {jobForm.atsPassThreshold}% will not be able to take the interview
                  </p>
                </div>
              </div>

              {/* Auto-Rejection Settings */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="block" size={20} className="text-orange-600" />
                  <h4 className="text-base font-semibold text-gray-900">Auto-Rejection Settings</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Automatically reject candidates who score below the threshold in AI assessment interviews
                </p>

                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="autoReject"
                    checked={jobForm.autoRejectEnabled}
                    onChange={(e) => setJobForm({ ...jobForm, autoRejectEnabled: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="autoReject" className="text-sm text-gray-700">
                    Enable automatic rejection based on interview assessment scores
                  </label>
                </div>

                {jobForm.autoRejectEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Rejection Threshold (%)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={jobForm.autoRejectThreshold}
                      onChange={(e) => setJobForm({ ...jobForm, autoRejectThreshold: parseInt(e.target.value) })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Candidates scoring below {jobForm.autoRejectThreshold}% will be automatically rejected
                    </p>
                  </div>
                )}
              </div>

              {/* Live AI Interview Settings */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="videocam" size={20} className="text-purple-600" />
                  <h4 className="text-base font-semibold text-gray-900">Live AI Interview Settings</h4>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">PREMIUM</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  Enable real-time voice interviews with AI for this position. Candidates will have live conversations with AI interviewer.
                </p>
                
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="liveInterview"
                    checked={jobForm.liveInterviewEnabled}
                    onChange={(e) => setJobForm({ ...jobForm, liveInterviewEnabled: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="liveInterview" className="text-sm text-gray-700">
                    Enable Live AI Interview for this job
                  </label>
                </div>

                {jobForm.liveInterviewEnabled && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Interview Duration (minutes)</label>
                        <Input
                          type="number"
                          min="3"
                          max="30"
                          value={jobForm.liveInterviewDuration}
                          onChange={(e) => setJobForm({ ...jobForm, liveInterviewDuration: parseInt(e.target.value) })}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Interview Language</label>
                        <select
                          value={jobForm.liveInterviewLanguage}
                          onChange={(e) => setJobForm({ ...jobForm, liveInterviewLanguage: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <option value="en-US">English (US)</option>
                          <option value="en-GB">English (UK)</option>
                          <option value="en-IN">English (India)</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4 pt-4 border-t">
                <Button type="submit" disabled={jobSubmitting} className="bg-gray-900 hover:bg-gray-800 text-white">
                  {jobSubmitting ? 'Creating...' : 'Create Job Post'}
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setJobFormOpen(false);
                    setJobForm(initialJobForm);
                    setJobFormError('');
                  }}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Jobs List */}
        <div className="space-y-4">
          {filteredJobs.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm p-12 text-center border border-gray-200">
              <Icon name="work_outline" size={48} className="text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No jobs found</h3>
              <p className="text-gray-600 mb-4">Start by creating your first job posting</p>
              <Button onClick={() => setJobFormOpen(true)} className="bg-gray-900 hover:bg-gray-800 text-white">
                <Icon name="add" size={20} className="mr-2" />
                Post New Job
              </Button>
            </div>
          ) : (
            filteredJobs.map((job) => (
              <div key={job.id} className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">{job.title}</h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(job.status)}`}>
                        {(job.status || 'Active').charAt(0).toUpperCase() + (job.status || 'Active').slice(1).toLowerCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                      <div className="flex items-center gap-1">
                        <Icon name="location_on" size={16} />
                        <span>{job.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="work" size={16} />
                        <span>{job.employment_type}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Icon name="schedule" size={16} />
                        <span>Posted {new Date(job.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                    <p className="text-gray-700 line-clamp-2">{job.description}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => handleStatusChange(job.id, job.status?.toLowerCase() === 'active' ? 'closed' : 'active')}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        job.status?.toLowerCase() === 'active'
                          ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                      title={job.status?.toLowerCase() === 'active' ? 'Close Job' : 'Activate Job'}
                    >
                      {job.status?.toLowerCase() === 'active' ? 'Close' : 'Activate'}
                    </button>
                    <button
                      onClick={() => navigate(`/jobs/${job.id}`)}
                      className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      title="View Details"
                    >
                      <Icon name="visibility" size={20} />
                    </button>
                    <button
                      onClick={() => openEditJobModal(job)}
                      className="p-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                      title="Edit Job"
                    >
                      <Icon name="edit" size={20} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirmJob(job)}
                      className="p-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                      title="Delete Job"
                    >
                      <Icon name="delete" size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Edit Job Modal */}
      {editingJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setEditingJob(null)}>
          <div
            className="relative w-full max-w-3xl rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Edit Job Post</h2>
              <button
                onClick={() => setEditingJob(null)}
                className="p-2 rounded-lg hover:bg-gray-100"
              >
                <Icon name="close" size={24} />
              </button>
            </div>

            <form onSubmit={handleUpdateJob} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Job Title *</label>
                  <Input
                    type="text"
                    value={editJobForm.title}
                    onChange={(e) => setEditJobForm({ ...editJobForm, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
                  <Input
                    type="text"
                    value={editJobForm.location}
                    onChange={(e) => setEditJobForm({ ...editJobForm, location: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
                  <Input
                    type="text"
                    value={editJobForm.department}
                    onChange={(e) => setEditJobForm({ ...editJobForm, department: e.target.value })}
                    placeholder="e.g. Product Engineering"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                  <select
                    value={editJobForm.category}
                    onChange={(e) => setEditJobForm({ ...editJobForm, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="IT">IT</option>
                    <option value="Non-IT">Non-IT</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Job Description *</label>
                <textarea
                  value={editJobForm.description}
                  onChange={(e) => setEditJobForm({ ...editJobForm, description: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
                  <select
                    value={editJobForm.employmentType}
                    onChange={(e) => setEditJobForm({ ...editJobForm, employmentType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Work Type</label>
                  <select
                    value={editJobForm.workType}
                    onChange={(e) => setEditJobForm({ ...editJobForm, workType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="on-site">On-site</option>
                    <option value="remote">Remote</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
                  <select
                    value={editJobForm.experienceLevel}
                    onChange={(e) => setEditJobForm({ ...editJobForm, experienceLevel: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tags (comma separated)</label>
                <Input
                  type="text"
                  value={editJobForm.tags}
                  onChange={(e) => setEditJobForm({ ...editJobForm, tags: e.target.value })}
                  placeholder="e.g. Python, React, AWS"
                />
              </div>

              {/* Interview Configuration */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="quiz" size={20} className="text-blue-600" />
                  <h4 className="text-base font-semibold text-gray-900">Interview Configuration</h4>
                </div>
                <p className="text-sm text-gray-600 mb-4">Customize the AI-generated interview questions for this role</p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Total Questions</label>
                    <Input
                      type="number"
                      min="1"
                      max="20"
                      value={editJobForm.totalQuestions}
                      onChange={(e) => setEditJobForm({ ...editJobForm, totalQuestions: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Technical Questions</label>
                    <Input
                      type="number"
                      min="0"
                      value={editJobForm.technicalQuestions}
                      onChange={(e) => setEditJobForm({ ...editJobForm, technicalQuestions: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Behavioral Questions</label>
                    <Input
                      type="number"
                      min="0"
                      value={editJobForm.behavioralQuestions}
                      onChange={(e) => setEditJobForm({ ...editJobForm, behavioralQuestions: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Situational Questions</label>
                    <Input
                      type="number"
                      min="0"
                      value={editJobForm.situationalQuestions}
                      onChange={(e) => setEditJobForm({ ...editJobForm, situationalQuestions: parseInt(e.target.value) || 0 })}
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Requirements / Custom Prompt
                    <span className="text-xs text-gray-500 ml-2">Guide AI question generation</span>
                  </label>
                  <textarea
                    value={editJobForm.additionalRequirements}
                    onChange={(e) => setEditJobForm({ ...editJobForm, additionalRequirements: e.target.value })}
                    placeholder="E.g., Focus on cloud architecture experience, ask about microservices..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* ATS Pass Threshold */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="verified" size={20} className="text-blue-600" />
                  <h4 className="text-base font-semibold text-gray-900">ATS Pass Threshold</h4>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum ATS Score (%)</label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={editJobForm.atsPassThreshold}
                    onChange={(e) => setEditJobForm({ ...editJobForm, atsPassThreshold: parseInt(e.target.value) || 50 })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Candidates with ATS score below {editJobForm.atsPassThreshold}% will not be able to take the interview
                  </p>
                </div>
              </div>

              {/* Auto-Rejection Settings */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="block" size={20} className="text-orange-600" />
                  <h4 className="text-base font-semibold text-gray-900">Auto-Rejection Settings</h4>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="editAutoReject"
                    checked={editJobForm.autoRejectEnabled}
                    onChange={(e) => setEditJobForm({ ...editJobForm, autoRejectEnabled: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="editAutoReject" className="text-sm text-gray-700">
                    Enable automatic rejection based on interview assessment scores
                  </label>
                </div>

                {editJobForm.autoRejectEnabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rejection Threshold (%)</label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      value={editJobForm.autoRejectThreshold}
                      onChange={(e) => setEditJobForm({ ...editJobForm, autoRejectThreshold: parseInt(e.target.value) || 0 })}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Candidates scoring below {editJobForm.autoRejectThreshold}% will be automatically rejected
                    </p>
                  </div>
                )}
              </div>

              {/* Live AI Interview Settings */}
              <div className="border-t pt-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="videocam" size={20} className="text-purple-600" />
                  <h4 className="text-base font-semibold text-gray-900">Live AI Interview Settings</h4>
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded">PREMIUM</span>
                </div>

                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="editLiveInterview"
                    checked={editJobForm.liveInterviewEnabled}
                    onChange={(e) => setEditJobForm({ ...editJobForm, liveInterviewEnabled: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="editLiveInterview" className="text-sm text-gray-700">
                    Enable Live AI Interview for this job
                  </label>
                </div>

                {editJobForm.liveInterviewEnabled && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Interview Duration (minutes)</label>
                      <Input
                        type="number"
                        min="3"
                        max="30"
                        value={editJobForm.liveInterviewDuration}
                        onChange={(e) => setEditJobForm({ ...editJobForm, liveInterviewDuration: parseInt(e.target.value) || 5 })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Interview Language</label>
                      <select
                        value={editJobForm.liveInterviewLanguage}
                        onChange={(e) => setEditJobForm({ ...editJobForm, liveInterviewLanguage: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="en-US">English (US)</option>
                        <option value="en-GB">English (UK)</option>
                        <option value="en-IN">English (India)</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {editJobError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {editJobError}
                </div>
              )}

              <div className="flex items-center gap-4 pt-4 border-t">
                <Button type="submit" disabled={editJobSubmitting} className="bg-gray-900 hover:bg-gray-800 text-white">
                  {editJobSubmitting ? 'Updating...' : 'Update Job'}
                </Button>
                <button
                  type="button"
                  onClick={() => setEditingJob(null)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setDeleteConfirmJob(null)}>
          <div 
            className="relative w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-xl font-bold text-gray-900 mb-4">Delete Job Post</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete "{deleteConfirmJob.title}"? This action cannot be undone.
            </p>
            <div className="flex items-center gap-4">
              <Button
                onClick={() => handleDeleteJob(deleteConfirmJob.id)}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                Delete Job
              </Button>
              <button
                onClick={() => setDeleteConfirmJob(null)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </HrDashboardLayout>
  );
};

export default HrJobsPage;
